export type OtsProgressBarTone =
  | "water-near-capacity"
  | "oil-near-capacity"
  | "water"
  | "oil"
  | "neutral"
  | "primary"
  | "success"
  | "warning"
  | "danger";
export type OtsProgressBarSize = "compact" | "default";

export type OtsProgressBarProps = {
  /** Completed amount. Leave value or max out for an indeterminate bar. */
  value?: number;
  max?: number;
  /** Names the bar for assistive technology, e.g. "Company site report progress". */
  label: string;
  /** Spoken in place of the percentage, e.g. "3 of 12 regions". */
  valueText?: string;
  tone?: OtsProgressBarTone;
  size?: OtsProgressBarSize;
  className?: string;
};

const toneClasses: Record<OtsProgressBarTone, string> = {
  water: "bg-[var(--ots-transport-category-water)]",
  oil: "bg-[var(--ots-load-oil)]",
  "water-near-capacity": "bg-[var(--ots-load-water-near-capacity)]",
  "oil-near-capacity": "bg-[var(--ots-load-oil-near-capacity)]",
  neutral: "bg-[var(--ots-text-muted)]",
  primary: "bg-[var(--ots-primary)]",
  success: "bg-[var(--ots-success)]",
  warning: "bg-[var(--ots-warning)]",
  danger: "bg-[var(--ots-error)]",
};

/**
 * Foundational theme-aware progress bar. Determinate when given a value and max, otherwise an indeterminate
 * sweep for work whose size isn't known yet. The sweep respects reduced motion.
 */
export const OtsProgressBar = ({
  value,
  max,
  label,
  valueText,
  tone = "primary",
  size = "default",
  className,
}: OtsProgressBarProps) => {
  const determinate = value !== undefined && max !== undefined && max > 0;
  const percent = determinate ? Math.min(100, Math.max(0, (value / max) * 100)) : undefined;

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={determinate ? 0 : undefined}
      aria-valuemax={determinate ? 100 : undefined}
      aria-valuenow={percent !== undefined ? Math.round(percent) : undefined}
      aria-valuetext={valueText}
      data-indeterminate={determinate ? undefined : "true"}
      className={[
        "relative w-full overflow-hidden rounded-full bg-[var(--ots-neutral-container)]",
        size === "compact" ? "h-1" : "h-1.5",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {determinate ? (
        <div
          className={[
            "h-full rounded-full transition-[width] duration-300 motion-reduce:transition-none",
            toneClasses[tone],
          ].join(" ")}
          style={{width: `${percent}%`}}
        />
      ) : (
        <div
          className={[
            "absolute inset-y-0 w-1/3 rounded-full animate-[ots-progress-sweep_1.4s_ease-in-out_infinite]",
            "motion-reduce:animate-none motion-reduce:left-0 motion-reduce:w-full motion-reduce:opacity-60",
            toneClasses[tone],
          ].join(" ")}
        />
      )}
      <style>{"@keyframes ots-progress-sweep{0%{left:-33%}100%{left:100%}}"}</style>
    </div>
  );
};
